// src/pages/Dashboard.jsx
import { useMemo } from "react";
import { getGreeting, calcNetWorthBreakEven } from "../lib/utils";
import { NetWorthCard } from "../components/dashboard/NetWorthCard";
import {
  AccountsCard,
  CreditCardsCard,
  LoansCard,
  CashFlowCard,
  CashCard,
  TopSpendingCard,
} from "../components/dashboard/DashboardCards";
import { Skeleton } from "../components/ui/primitives";
import {
  useAccounts,
  useLoans,
  useCashBalance,
  useSpendingByCategory,
} from "../hooks/useFinancialData";

// Demo data used until Plaid is connected
const DEMO_ACCOUNTS = [
  { id: "hhcu_checking", name: "HHCU Checking", balance: 2140, mask: "6432", subtype: "checking" },
  { id: "hhcu_savings", name: "HHCU Savings", balance: 8420, mask: "6431", subtype: "savings" },
  { id: "usaa_savings", name: "USAA Savings", balance: 3200, mask: "4491", subtype: "savings" },
];

const DEMO_CARDS = [
  { id: "usaa_cc", name: "USAA Credit Card", balance: -312, apr: 19.9, limit: 5000, dueDate: new Date(2025, 4, 12) },
  { id: "rh_cc", name: "Robinhood Credit Card", balance: -890, apr: 24.99, limit: 8000, dueDate: new Date(2025, 4, 15) },
];

const DEMO_LOANS = [
  { id: "plus", name: "Parent Plus Loan", balance: -12800, interestRate: 7.54, monthlyPayment: 145, interestPaid: 2100 },
  { id: "sub1", name: "Subsidized Loan 1", balance: -3200, interestRate: 4.5, monthlyPayment: 33, interestPaid: 480 },
  { id: "sub2", name: "Subsidized Loan 2", balance: -2800, interestRate: 5.05, monthlyPayment: 30, interestPaid: 390 },
  { id: "sub3", name: "Subsidized Loan 3", balance: -2600, interestRate: 5.5, monthlyPayment: 28, interestPaid: 340 },
  { id: "unsub4", name: "Unsubsidized Loan 4", balance: -2400, interestRate: 6.0, monthlyPayment: 27, interestPaid: 310 },
  { id: "unsub5", name: "Unsubsidized Loan 5", balance: -4600, interestRate: 6.54, monthlyPayment: 40, interestPaid: 620 },
];

const DEMO_SPENDING = [
  { category: "Eating out", total: 380, count: 42 },
  { category: "Sports betting", total: 290, count: 18 },
  { category: "Groceries", total: 180, count: 8 },
  { category: "Gas / fuel", total: 120, count: 6 },
  { category: "Subscriptions", total: 60, count: 3 },
];

const DEMO_PREV_SPENDING = {
  "Eating out": 283,
  "Sports betting": 330,
  Groceries: 196,
  "Gas / fuel": 114,
  Subscriptions: 61,
};

export function Dashboard() {
  const now = new Date();
  const { accounts, loading: acctLoading } = useAccounts();
  const { loans, loading: loanLoading } = useLoans();
  const { cash, loading: cashLoading } = useCashBalance();
  const { spending } = useSpendingByCategory(now.getMonth(), now.getFullYear());

  const displayAccounts = accounts.length ? accounts.filter(a => !a.type?.includes("credit")) : DEMO_ACCOUNTS;
  const displayCards = accounts.length ? accounts.filter(a => a.type === "credit") : DEMO_CARDS;
  const displayLoans = loans.length ? loans : DEMO_LOANS;
  const displaySpending = spending.length ? spending : DEMO_SPENDING;

  const totalAssets = useMemo(() => {
    const acctTotal = displayAccounts.reduce((s, a) => s + (a.balance || 0), 0);
    const cashAmt = cash?.amount || 180;
    return acctTotal + cashAmt;
  }, [displayAccounts, cash]);

  const totalLiabilities = useMemo(() => {
    const cardTotal = displayCards.reduce((s, c) => s + Math.abs(c.balance || 0), 0);
    const loanTotal = displayLoans.reduce((s, l) => s + Math.abs(l.balance || 0), 0);
    return cardTotal + loanTotal;
  }, [displayCards, displayLoans]);

  const breakEvenDate = calcNetWorthBreakEven(totalAssets - totalLiabilities, 340 / 4);

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3 flex justify-between items-center">
        <div>
          <p className="text-[11px] text-text-tertiary font-mono">
            {getGreeting()},
          </p>
          <h1 className="font-display text-lg font-bold text-text-primary leading-tight">
            Gavin
          </h1>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-text-tertiary">Last sync</p>
          <p className="text-[11px] text-accent-green font-mono">2h ago</p>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        <NetWorthCard
          totalAssets={totalAssets}
          totalLiabilities={totalLiabilities}
          breakEvenDate={breakEvenDate}
        />

        <AccountsCard accounts={displayAccounts} />

        <CreditCardsCard cards={displayCards} />

        <LoansCard loans={displayLoans} />

        <CashFlowCard
          primaryIncome={4200}
          altIncome={180}
          totalSpending={3190}
        />

        <TopSpendingCard
          spending={displaySpending}
          previousSpending={DEMO_PREV_SPENDING}
        />

        <CashCard cash={cash} />
      </div>
    </div>
  );
}
